import type { FC } from "react";

import {
  Box,
  type BoxProps,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Row,
  Text,
  MeetingIcon,
  useDisclosure,
} from "@hightouch/htui";
import { format } from "date-fns";

import { Calendar, type Props as CalendarProps } from "src/ui/calendar";
import { TimeSelect } from "src/ui/time-select";

export type Props = {
  hideTime?: boolean;
  onSelectClose?: (elementId: string) => void;
  onSelectOpen?: (elementId: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  hideTimeZone?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  dateFormat?: string;
  inputWidth?: string;
} & CalendarProps &
  Omit<BoxProps, "onChange">;

export const DateTimeSelect: FC<Readonly<Props>> = ({
  hideTime,
  value,
  onChange,
  onSelectClose,
  onSelectOpen,
  minDate,
  maxDate,
  isClearable,
  placeholder = "Select a date…",
  isDisabled = false,
  isInvalid = false,
  hideTimeZone = false,
  size = "md",
  dateFormat = "PPP",
  inputWidth = "auto",
  ...props
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();

  return (
    <Row align="center" flexShrink={0} {...props}>
      <Popover isLazy isOpen={isOpen} onClose={onClose} placement="bottom">
        <PopoverTrigger>
          <Box
            as={Button}
            isDisabled={isDisabled}
            color={value ? "text.primary" : "text.placeholder"}
            px={2}
            gap={2}
            alignItems="center"
            justifyContent="flex-start"
            size={size}
            width={inputWidth}
            fontWeight="normal"
            borderWidth="hairline"
            borderStyle="solid"
            borderColor={
              isInvalid
                ? "danger.base"
                : isOpen
                  ? "primary.base"
                  : "base.border"
            }
            boxShadow={isOpen ? (isInvalid ? "outlineRed" : "outline") : "none"}
            borderRadius="lg"
            overflow="hidden"
            whiteSpace="nowrap"
            transitionProperty="border-color, background-color, color"
            transitionDuration="fast"
            transitionTimingFunction="easeOutQuad"
            _hover={{
              borderColor: isInvalid
                ? "danger.base"
                : isOpen
                  ? "primary.base"
                  : "gray.border",
              bg: "base.lightBackground",
              color: value ? "text.primary" : "text.placeholder",
            }}
            _active={{
              borderWidth: "hairline",
              borderStyle: "solid",
              borderColor: isInvalid ? "danger.base" : "primary.base",
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggle();
            }}
          >
            <Box as={MeetingIcon} color="text.tertiary" flexShrink={0} />
            {value ? format(value, dateFormat) : placeholder}
          </Box>
        </PopoverTrigger>

        <PopoverContent p={2} width="fit-content">
          <Calendar
            minDate={minDate}
            maxDate={maxDate}
            value={value}
            onChange={(value) => {
              onChange(value);
              onClose();
            }}
            isClearable={isClearable}
          />
        </PopoverContent>
      </Popover>
      {!hideTime && (
        <>
          <Text mx={2}>at</Text>
          <TimeSelect
            value={value}
            onChange={onChange}
            onSelectClose={onSelectClose}
            onSelectOpen={onSelectOpen}
            isDisabled={isDisabled}
            hideTimeZone={hideTimeZone}
          />
        </>
      )}
    </Row>
  );
};
